import { AnsweringSubscriptionTag, BuildJsonType } from '@bb/domain/dist/types';
import React, { useEffect, useState } from 'react';
import { Divider, Pagination } from 'components';
import { useDebounce } from 'hooks';
import { orgs as searchOrgs } from 'api/SearchController';
import errorReporter from '@app/errorReporter';
import { OrgDoc } from '@bb/domain/dist/schemas';
import BrokeragesListItems from './BrokeragesListItems';
import BrokeragesListFilter from './BrokeragesListFilter';

type AlphMapType = {
  [key: string]: BuildJsonType<
    OrgDoc & { subscriptionTag?: AnsweringSubscriptionTag }
  >[];
};

const alphKeys = ['#', ...Array(26).fill(false)].map(
  (char, i) => char || String.fromCharCode(64 + i),
);

const ORGS_LIMIT = 20;

const BrokeragesList: React.FC = () => {
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [alphMap, setAlphMap] = useState<AlphMapType>({});
  const [loading, setLoading] = useState(true);

  const debouncedText = useDebounce(filterText);

  const handlePageChange = (selectedPage: number) => {
    if (loading) return;
    setPage(selectedPage);
  };

  const handleTextChange = (text: string) => {
    setFilterText(text);
    setPage(1);
  };

  useEffect(() => {
    const initFunc = async () => {
      setLoading(true);
      const newAlphMap: AlphMapType = {};
      alphKeys.forEach(char => {
        newAlphMap[char] = [];
      });
      let newTotal = 0;

      try {
        const { orgs, count } = await searchOrgs({
          input: debouncedText,
          limit: ORGS_LIMIT,
          from: (page - 1) * ORGS_LIMIT,
          sortField: 'name',
          sortDir: 'asc',
        });

        orgs.forEach(org => {
          if (org.name && org.name.charAt(0).match(/[a-zA-Z]/)) {
            const key = org.name.charAt(0).toUpperCase();
            newAlphMap[key].push(org);
          } else {
            newAlphMap['#'].push(org);
          }
        });
        newTotal = count;
      } catch (err) {
        errorReporter.error(`Error fetching answering brokerages: ${err}`);
      }

      setAlphMap(newAlphMap);
      setTotal(newTotal);
      setLoading(false);
    };

    initFunc();
  }, [debouncedText, page]);

  return (
    <div>
      <BrokeragesListFilter
        setFilterText={handleTextChange}
        filterText={filterText}
      />
      <div className="content animate-panel">
        <div className="hpanel">
          <div className="panel-body">
            <BrokeragesListItems
              isLoaded={!loading}
              alphKeys={alphKeys}
              alphMap={alphMap}
            />
            <Divider />
            <Pagination
              current={page}
              total={total}
              pageSize={ORGS_LIMIT}
              showSizeChanger={false}
              onChange={handlePageChange}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokeragesList;
